/**
 * Security Monitoring and Alerting System
 * Tracks security events and anomalies in real-time
 */

// Create a browser-compatible EventEmitter
class EventEmitter {
  private events: { [key: string]: ((...args: any[]) => void)[] } = {}

  on(event: string, listener: (...args: any[]) => void) {
    if (!this.events[event]) {
      this.events[event] = []
    }
    this.events[event].push(listener)
  }

  emit(event: string, ...args: any[]) {
    if (!this.events[event]) return
    this.events[event].forEach(listener => listener(...args))
  }

  off(event: string, listener: (...args: any[]) => void) {
    if (!this.events[event]) return
    this.events[event] = this.events[event].filter(l => l !== listener)
  }
}

export interface SecurityEvent {
  type: SecurityEventType
  severity: 'low' | 'medium' | 'high' | 'critical'
  timestamp: Date
  details: any
  source: string
  sessionId?: string
  userId?: string
}

export enum SecurityEventType {
  // Entropy events
  LOW_ENTROPY = 'low_entropy',
  ENTROPY_FAILURE = 'entropy_failure',
  
  // Key generation events
  KEY_GENERATION = 'key_generation',
  KEY_GENERATION_FAILURE = 'key_generation_failure',
  
  // Signature events
  SIGNATURE_CREATED = 'signature_created',
  SIGNATURE_VERIFIED = 'signature_verified',
  SIGNATURE_INVALID = 'signature_invalid',
  
  // Attack detection
  TIMING_ANOMALY = 'timing_anomaly',
  FAULT_DETECTED = 'fault_detected',
  MEMORY_CORRUPTION = 'memory_corruption',
  
  // API security
  RATE_LIMIT_EXCEEDED = 'rate_limit_exceeded',
  INVALID_ORIGIN = 'invalid_origin',
  SUSPICIOUS_PATTERN = 'suspicious_pattern',
  
  // System events
  SECURITY_INITIALIZED = 'security_initialized',
  SECURITY_ERROR = 'security_error'
}

export class SecurityMonitor extends EventEmitter {
  private static instance: SecurityMonitor
  private eventLog: SecurityEvent[] = []
  private sessionId: string
  private alertThresholds = {
    low: 100,
    medium: 50,
    high: 10,
    critical: 1
  }
  private eventCounts = new Map<string, number>()
  
  private constructor() {
    super()
    this.sessionId = this.generateSessionId()
    this.startMonitoring()
  }
  
  static getInstance(): SecurityMonitor {
    if (!SecurityMonitor.instance) {
      SecurityMonitor.instance = new SecurityMonitor()
    }
    return SecurityMonitor.instance
  }
  
  /**
   * Log a security event
   */
  logEvent(event: Omit<SecurityEvent, 'timestamp' | 'sessionId'>): void {
    const fullEvent: SecurityEvent = {
      ...event,
      timestamp: new Date(),
      sessionId: this.sessionId
    }
    
    this.eventLog.push(fullEvent)
    this.emit('security-event', fullEvent)
    
    // Update event counts
    const key = `${event.type}_${event.severity}`
    this.eventCounts.set(key, (this.eventCounts.get(key) || 0) + 1)
    
    // Check thresholds
    this.checkThresholds(fullEvent)
    
    // Log to console in development
    if (import.meta.env.MODE === 'development') {
      console.log(`[SECURITY ${event.severity.toUpperCase()}]`, event.type, event.details)
    }
    
    // Send to external monitoring in production
    if (import.meta.env.MODE === 'production') {
      this.sendToMonitoring(fullEvent)
    }
  }
  
  /**
   * Get recent events
   */
  getRecentEvents(minutes: number = 5): SecurityEvent[] {
    const cutoff = new Date(Date.now() - minutes * 60 * 1000)
    return this.eventLog.filter(e => e.timestamp > cutoff)
  }
  
  /**
   * Get events by type
   */
  getEventsByType(type: SecurityEventType): SecurityEvent[] {
    return this.eventLog.filter(e => e.type === type)
  }
  
  /**
   * Get security metrics
   */
  getMetrics() {
    const recentEvents = this.getRecentEvents(60) // Last hour
    
    const metrics = {
      totalEvents: this.eventLog.length,
      recentEvents: recentEvents.length,
      eventsByType: {} as Record<string, number>,
      eventsBySeverity: {} as Record<string, number>,
      anomalyScore: 0,
      healthStatus: 'healthy' as 'healthy' | 'warning' | 'critical'
    }
    
    // Count by type
    for (const event of recentEvents) {
      metrics.eventsByType[event.type] = (metrics.eventsByType[event.type] || 0) + 1
      metrics.eventsBySeverity[event.severity] = (metrics.eventsBySeverity[event.severity] || 0) + 1
    }
    
    // Calculate anomaly score
    metrics.anomalyScore = this.calculateAnomalyScore(recentEvents)
    
    // Determine health status
    if (metrics.anomalyScore > 0.8 || metrics.eventsBySeverity.critical > 0) {
      metrics.healthStatus = 'critical'
    } else if (metrics.anomalyScore > 0.5 || metrics.eventsBySeverity.high > 2) {
      metrics.healthStatus = 'warning'
    }
    
    return metrics
  }
  
  /**
   * Check if thresholds are exceeded
   */
  private checkThresholds(event: SecurityEvent): void {
    const threshold = this.alertThresholds[event.severity]
    const key = `${event.type}_${event.severity}`
    const count = this.eventCounts.get(key) || 0
    
    if (count >= threshold) {
      this.triggerAlert({
        type: 'threshold_exceeded',
        event: event.type,
        severity: event.severity,
        count,
        threshold
      })
    }
  }
  
  /**
   * Calculate anomaly score based on recent events
   */
  private calculateAnomalyScore(events: SecurityEvent[]): number {
    if (events.length === 0) return 0
    
    let score = 0
    const weights = {
      low: 0.1,
      medium: 0.3,
      high: 0.6,
      critical: 1.0
    }
    
    // Weight by severity
    for (const event of events) {
      score += weights[event.severity]
    }
    
    // Normalize by expected rate (10 low events per hour is normal)
    const normalizedScore = score / (events.length * 0.1)
    
    // Check for patterns
    const patternScore = this.detectPatterns(events)
    
    return Math.min(1, (normalizedScore + patternScore) / 2)
  }
  
  /**
   * Detect suspicious patterns in events
   */
  private detectPatterns(events: SecurityEvent[]): number {
    let patternScore = 0
    
    // Check for rapid repeated events
    const eventTimes = events.map(e => e.timestamp.getTime())
    for (let i = 1; i < eventTimes.length; i++) {
      if (eventTimes[i] - eventTimes[i-1] < 1000) { // Less than 1 second
        patternScore += 0.1
      }
    }
    
    // Check for unusual event combinations
    const typeSequence = events.map(e => e.type)
    if (typeSequence.includes(SecurityEventType.ENTROPY_FAILURE) && 
        typeSequence.includes(SecurityEventType.KEY_GENERATION)) {
      patternScore += 0.5 // Suspicious: key gen after entropy failure
    }
    
    return Math.min(1, patternScore)
  }
  
  /**
   * Trigger security alert
   */
  private triggerAlert(alert: any): void {
    this.emit('security-alert', alert)
    
    // In production, send to alerting service
    if (import.meta.env.MODE === 'production') {
      this.sendAlert(alert)
    }
  }
  
  /**
   * Send event to external monitoring
   */
  private async sendToMonitoring(event: SecurityEvent): Promise<void> {
    // Implement integration with monitoring service
    // Example: Datadog, Sentry, CloudWatch, etc.
    try {
      // await fetch(process.env.MONITORING_ENDPOINT, {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(event)
      // })
    } catch (error) {
      console.error('Failed to send security event to monitoring:', error)
    }
  }
  
  /**
   * Send alert to notification service
   */
  private async sendAlert(alert: any): Promise<void> {
    // Implement integration with alerting service
    // Example: PagerDuty, Slack, email, etc.
    try {
      // await fetch(process.env.ALERT_ENDPOINT, {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(alert)
      // })
    } catch (error) {
      console.error('Failed to send security alert:', error)
    }
  }
  
  /**
   * Start periodic monitoring tasks
   */
  private startMonitoring(): void {
    // Periodic health check
    setInterval(() => {
      const metrics = this.getMetrics()
      if (metrics.healthStatus !== 'healthy') {
        this.logEvent({
          type: SecurityEventType.SECURITY_ERROR,
          severity: metrics.healthStatus === 'critical' ? 'critical' : 'high',
          details: { metrics },
          source: 'health-check'
        })
      }
    }, 60000) // Every minute
    
    // Clean old events
    setInterval(() => {
      const cutoff = new Date(Date.now() - 24 * 60 * 60 * 1000) // 24 hours
      this.eventLog = this.eventLog.filter(e => e.timestamp > cutoff)
    }, 3600000) // Every hour
  }
  
  /**
   * Generate unique session ID
   */
  private generateSessionId(): string {
    return `sec_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }
  
  /**
   * Export security report
   */
  exportReport(): string {
    const metrics = this.getMetrics()
    const report = {
      sessionId: this.sessionId,
      generatedAt: new Date().toISOString(),
      metrics,
      recentEvents: this.getRecentEvents(60),
      recommendations: this.generateRecommendations(metrics)
    }
    
    return JSON.stringify(report, null, 2)
  }
  
  /**
   * Generate security recommendations
   */
  private generateRecommendations(metrics: any): string[] {
    const recommendations: string[] = []
    
    if (metrics.eventsBySeverity.critical > 0) {
      recommendations.push('CRITICAL: Investigate critical security events immediately')
    }
    
    if (metrics.anomalyScore > 0.7) {
      recommendations.push('HIGH: Anomalous activity detected - review security logs')
    }
    
    if (metrics.eventsByType[SecurityEventType.LOW_ENTROPY] > 5) {
      recommendations.push('MEDIUM: Multiple low entropy events - check RNG sources')
    }
    
    if (metrics.eventsByType[SecurityEventType.RATE_LIMIT_EXCEEDED] > 10) {
      recommendations.push('MEDIUM: High rate limit violations - possible DoS attempt')
    }
    
    return recommendations
  }
}

// Helper function for easy logging
export function logSecurityEvent(
  type: SecurityEventType,
  severity: 'low' | 'medium' | 'high' | 'critical',
  details: any,
  source: string
): void {
  SecurityMonitor.getInstance().logEvent({ type, severity, details, source })
}

export default SecurityMonitor